// The RF sensors this screen can name, loaded once and shared — so twenty RfV
// lines in the Message Box and every receiver card on a page cost one request.
//
// Scope:
//   all          every sensor (Super Admin — the Message Box); also loads the
//                signature library so an unlabelled code gets a "probably …"
//   receiverUno  only what that receiver last heard (the receiver card)
//
// ⚠️ A NEW CODE MEANS THE LIST IS OLD. The server creates a sensor row the
// first time it hears a transmitter, so a code we cannot place is a reason to
// reload — throttled to once per 30 s per scope, or a chatty receiver would
// reload on every frame.

import { useCallback, useEffect, useState } from 'react';
import { getRfDevices } from '../api/api';
import { buildRfDirectory, describeRfCode } from '../devices/rfIdentify';
import { loadSignatures } from '../devices/rfSignatures';

const RELOAD_MS = 30 * 1000;
const cache = new Map();      // key -> { promise, at }
const listeners = new Map();  // key -> Set<fn>
const bodies = new Map();     // key -> request body, so a watched list can be reloaded

function keyOf({ all, receiverUno }) {
  return all ? 'all' : `rx:${receiverUno || ''}`;
}

function load(key, body) {
  bodies.set(key, body);
  const promise = getRfDevices(body)
    .then((r) => buildRfDirectory(Array.isArray(r?.data) ? r.data : []))
    .catch(() => buildRfDirectory([]));
  cache.set(key, { promise, at: Date.now() });
  promise.then((dir) => (listeners.get(key) || new Set()).forEach((fn) => fn(dir)));
  return promise;
}

/**
 * Drop every cached list — after a scan, a rename or a replace — and reload the
 * ones a screen is showing right now, or an open Message Box keeps saying
 * "New sensor" about the sensor that was just saved.
 */
export function invalidateRfDirectory() {
  cache.clear();
  listeners.forEach((set, key) => {
    if (set.size && bodies.has(key)) load(key, bodies.get(key));
  });
}

export default function useRfDirectory({ all = false, receiverUno = '' } = {}) {
  const key = keyOf({ all, receiverUno });
  const enabled = all || !!receiverUno;
  const [dir, setDir] = useState(null);
  const [sig, setSig] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const body = all ? {} : { ReceiverUniqueNumber: receiverUno };
    const onDir = (d) => { if (alive) setDir(d); };
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(onDir);
    (cache.get(key)?.promise || load(key, body)).then(onDir);
    if (all) loadSignatures().then((s) => { if (alive) setSig(s); });
    return () => { alive = false; listeners.get(key)?.delete(onDir); };
  }, [key, enabled, all, receiverUno]);

  /** Reload if the list is older than RELOAD_MS. */
  const refreshIfStale = useCallback(() => {
    if (!enabled) return;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < RELOAD_MS) return;
    load(key, all ? {} : { ReceiverUniqueNumber: receiverUno });
  }, [key, enabled, all, receiverUno]);

  const describe = useCallback((code) => describeRfCode(code, dir, sig), [dir, sig]);

  return {
    describe, refreshIfStale, ready: !!dir,
  };
}
